import React from "react";
import moment, { type Moment } from "moment";
import { getOption } from "../../../Utilities/Platform/Options/Options";
import Button from "../../Buttons/Button";
import DatePickerDay from "./DatePickerDay";

type Props = {
    month: Moment;
    start: Moment | null;
    end: Moment | null;
    range: boolean;
    hover: Moment | null;
    onClick: (value: Moment) => void;
    onHeaderClick: (e: React.MouseEvent<HTMLButtonElement>) => void;
    onHoverChange: (hover: Moment | null) => void;
};

type RangePositionType = {
    x: number;
    y: number;
    cap: boolean;
};

export type MonthStateType = {
    name: string;
    days: number;
    startOf: number;
};

type State = {
    month: MonthStateType;
    start: RangePositionType | null;
    end: RangePositionType | null;
    hover: RangePositionType | null;
};

function prepareMonth(value: Moment, startOfWeek: Moment): MonthStateType {
    const month = value.clone().startOf("month");

    let startOf = month.day() - startOfWeek.day();

    if(startOf < 0) {
        startOf += 7;
    }

    startOf %= 7;

    return {
        name: month.format("MMMM YYYY"),
        days: month.daysInMonth(),
        startOf,
    };
}

export default class DatePickerMonth extends React.Component<Props, State> {
    public startOfWeek: Moment;

    constructor(props: Readonly<Props>) {
        super(props);

        this.startOfWeek = moment().day(getOption("start_of_week"));

        const month = prepareMonth(props.month, this.startOfWeek);

        this.state = {
            start: this.prepareStartPosition(month.startOf, props.start),
            end  : this.prepareRangePosition(month.startOf, props.end, props.start),
            hover: this.prepareRangePosition(month.startOf, props.hover, props.start),
            month,
        };
    }

    public componentDidUpdate(prevProps: Readonly<Props>): void {
        if(prevProps.month !== this.props.month) {
            this.setState({
                month: prepareMonth(this.props.month, this.startOfWeek),
            }, () => {
                this.setState({
                    start: this.prepareStartPosition(this.state.month.startOf, this.props.start),
                    end  : this.prepareRangePosition(this.state.month.startOf, this.props.end, this.props.start),
                    hover: this.prepareRangePosition(this.state.month.startOf, this.props.hover, this.props.start),
                });
            });
        }

        if(
            prevProps.start !== this.props.start ||
            prevProps.end !== this.props.end && this.props.range ||
            prevProps.hover !== this.props.hover && this.props.range
        ) {
            this.setState({
                start: this.prepareStartPosition(this.state.month.startOf, this.props.start),
                end  : this.prepareRangePosition(this.state.month.startOf, this.props.end, this.props.start),
                hover: this.prepareRangePosition(this.state.month.startOf, this.props.hover, this.props.start),
            });
        }
    }

    public prepareStartPosition = (offset: number, value: Moment | null): null | RangePositionType => {
        if(!moment.isMoment(value)) {
            return null;
        }

        let compare = this.props.end;

        if(!this.props.end && moment.isMoment(this.props.hover)) {
            compare = this.props.hover;
        }

        return this.prepareRangePosition(offset, value, compare);
    };

    public prepareRangePosition = (
        offset: number,
        value: Moment | null,
        compare: Moment | null
    ): RangePositionType | null => {
        if(!moment.isMoment(value) || !moment.isMoment(compare)) {
            return null;
        }

        // Check to see which direction to calculate
        const end = compare.isBefore(value);

        // Check to see if this month is in the range
        if(end) {
            if(!this.props.month.isBetween(compare, value, "month", "[]")) {
                return null;
            }
        } else {
            if(!this.props.month.isBetween(value, compare, "month", "[]")) {
                return null;
            }
        }

        let date = 1;
        let cap = false;

        if(value.isSame(this.props.month, "month")) {
            date = value.date();
            cap = true;
        } else if(value.isBefore(this.props.month, "month")) {
            if(end) {
                return null;
            }

            date = 1; // Start of month
        } else if(value.isAfter(this.props.month, "month")) {
            if(!end) {
                return null;
            }

            date = this.props.month.daysInMonth(); // End of month
        }

        date += offset - 1;

        return {
            x: date % 7 + 1,
            y: Math.floor(date / 7),
            cap,
        };
    };

    public render(): React.ReactNode {
        const days = [];

        for(let i = 0, l = 7; i < l; i++) {
            const day = this.startOfWeek.clone().add(i, "days").format("ddd");

            days.push(
                <div
                    key={day}
                    className="calendar-date-picker-month-day-name"
                    style={{
                        gridRow   : "1 / span 1",
                        gridColumn: `${i + 1} / span 1`,
                    }}
                >
                    {day}
                </div>
            );
        }

        for(let i = 1, l = this.state.month.days; i <= l; i++) {
            days.push(
                <DatePickerDay
                    key={`${this.state.month.name}|${i}|day`}
                    day={i}
                    month={this.props.month}
                    onClick={this.props.onClick}
                    onHoverChange={this.props.onHoverChange}
                    range={this.props.range}
                    start={this.props.start}
                    end={this.props.end}
                    hover={this.props.hover}
                    row={Math.floor((i + this.state.month.startOf - 1) / 7) + 2}
                    column={(i + this.state.month.startOf - 1) % 7 + 1}
                />
            );
        }

        // Create the range indicators
        const rangeIndicators = [];

        if(this.props.range) {
            let { start, end } = this.state;
            let isHover = false;

            if(!end && this.props.hover) {
                isHover = true;
                end = this.state.hover;
            }

            if(start && end) {
                // Check to see if the indicator is after end
                if(start.y * 7 + start.x > end.y * 7 + end.x) {
                    const temp = start;

                    start = end;
                    end = temp;
                }

                const rows = end.y - start.y + 1;

                for(let i = 0; i < rows; i++) {
                    const classes = [ "calendar-date-picker-month-range-indicator" ];

                    let gridStart = 1;
                    let gridEnd = 8;

                    if(i === 0) {
                        gridStart = start.x;

                        if(start.cap) {
                            classes.push("start-cap");
                        }
                    }

                    if(i === rows - 1) {
                        gridEnd = end.x + 1;

                        if(end.cap) {
                            classes.push("end-cap");
                        }
                    }

                    if(isHover) {
                        classes.push("hover");
                    }

                    rangeIndicators.push(
                        <div
                            key={`${this.state.month.name}|${i}|range`}
                            className={classes.join(" ")}
                            style={{
                                gridColumnStart: gridStart,
                                gridColumnEnd  : gridEnd,
                                gridRow        : `${start.y + i + 2} / span 1`,
                            }}
                        />
                    );
                }
            }
        }

        return (
            <div className="calendar-date-picker-month">
                <Button
                    type="button"
                    classes={[ "calendar-date-picker-month-header" ]}
                    onClick={this.props.onHeaderClick}
                >
                    {this.state.month.name}
                </Button>
                <div className="calendar-date-picker-month-days">
                    {rangeIndicators}
                    {days}
                </div>
            </div>
        );
    }
}
